import { InvoiceInfoType } from '@clearcover/core-app-types';
import currency from 'currency.js';
import React, { FC } from 'react';
import styles from './styles.module.css';
import classNames from 'classnames';
import { Asset, InfoFilledIcon, Tooltip } from '@clearcover/atlas';
import { useAuth0 } from 'Auth';

export type ItemizedPaymentAmountProps = {
  paymentFeeEnabledState: boolean;
  paymentTypeFees: InvoiceInfoType['paymentTypeFees'];
  chosenPaymentMethodType: 'creditCard' | 'bank';
  itemizedAmounts: {
    productAmount: string;
    installmentFee: string;
    productFees: string;
    waivedFees?: string;
    memberSurplusContribution: string;
    taxesTotal: string;
  };
  totalAmount: currency;
};

function calculateTotalFees({
  paymentFeeEnabledState,
  paymentTypeFees,
  chosenPaymentMethodType,
  itemizedAmounts,
  totalAmount,
  isInternalUser,
}: {
  paymentFeeEnabledState: boolean;
  paymentTypeFees: InvoiceInfoType['paymentTypeFees'];
  chosenPaymentMethodType: 'creditCard' | 'bank';
  itemizedAmounts: {
    installmentFee: string;
    productFees: string;
  };
  totalAmount: currency;
  isInternalUser: boolean;
}): currency {
  if (totalAmount.value === 0) {
    return currency(0);
  }

  const processingFee =
    paymentFeeEnabledState && totalAmount.value !== 0
      ? currency(paymentTypeFees[chosenPaymentMethodType])
      : currency(itemizedAmounts.installmentFee);

  const convenienceFee = currency(
    isInternalUser ? paymentTypeFees.convenienceFee : '0'
  );

  const productFees = currency(itemizedAmounts.productFees);

  return processingFee.add(productFees).add(convenienceFee);
}

export const ItemizedPaymentAmounts: FC<ItemizedPaymentAmountProps> = ({
  paymentFeeEnabledState,
  paymentTypeFees,
  chosenPaymentMethodType,
  itemizedAmounts,
  totalAmount,
}) => {
  const { user: currentUser } = useAuth0();

  const productAmount = currency(itemizedAmounts.productAmount);
  const memberSurplusContribution = currency(
    itemizedAmounts.memberSurplusContribution
  );
  const taxesTotal = currency(itemizedAmounts.taxesTotal);
  const totalFees = calculateTotalFees({
    paymentFeeEnabledState,
    paymentTypeFees,
    chosenPaymentMethodType,
    itemizedAmounts,
    totalAmount,
    isInternalUser: currentUser?.isInternalUser || false,
  });
  const waivedFees =
    itemizedAmounts.waivedFees && currency(itemizedAmounts.waivedFees);

  return (
    <div className={styles.lineItems}>
      {productAmount.value !== 0 && (
        <div className={styles.lineItem} data-testid="payment-amount-premium">
          <span>Premium Cost</span>
          <span data-testids="payment-amount-premium-value">
            {productAmount.format(true)}
          </span>
        </div>
      )}

      {totalFees.value !== 0 && (
        <div className={styles.lineItem} data-testid={`payment-amount-fee`}>
          <span>Fees</span>
          <span data-testids={`payment-amount-fee-value`}>
            {totalFees.format(true)}
          </span>
        </div>
      )}

      {memberSurplusContribution.value !== 0 && (
        <div
          className={styles.lineItem}
          data-testid={`payment-amount-member-surplus-contribution`}
        >
          <span>Surplus Contribution</span>
          <span
            data-testids={`payment-amount-member-surplus-contribution-value`}
          >
            {memberSurplusContribution.format(true)}
          </span>
        </div>
      )}

      {taxesTotal.value !== 0 && (
        <div className={styles.lineItem} data-testid={`payment-amount-taxes`}>
          <span>Taxes</span>
          <span data-testids={`payment-amount-taxes-value`}>
            {taxesTotal.format(true)}
          </span>
        </div>
      )}

      {waivedFees && waivedFees.value !== 0 && (
        <div
          className={classNames(styles.lineItem, styles.success)}
          data-testid={`payment-amount-waived-fees`}
        >
          <span>Fee Savings</span>
          <Tooltip tooltip={<span>Amount saved on installment fees</span>}>
            <Asset
              icon={InfoFilledIcon}
              description={`tooltip`}
              height={16}
              width={16}
              className={styles.infoIcon}
            />
          </Tooltip>
          <span data-testids={`payment-amount-waived-fees-value`}>
            {waivedFees.format(true)}
          </span>
        </div>
      )}

      <div className={styles.lineItem} data-testid="payment-amount-total">
        <span>Payment total</span>
        <span data-testids="payment-amount-total-value">
          {totalAmount.format(true)}
        </span>
      </div>
    </div>
  );
};
